/**
 * @fileoverview Search Mode Toggle Component
 *
 * Toggle button group for selecting song search mode.
 * Allows users to switch between different search strategies.
 *
 * **Modes:**
 * - 'both': Search both Spanish lyrics and English translations
 * - 'spanish': Search Spanish lyrics only
 * - 'metadata': Search song titles, verbs, and tags
 *
 * @module components/SearchModeToggle
 */

export type SearchMode = 'both' | 'spanish' | 'metadata'

interface SearchModeToggleProps {
  mode: SearchMode
  onChange: (mode: SearchMode) => void
  className?: string
}

const modes: { value: SearchMode; label: string; description: string }[] = [
  {
    value: 'both',
    label: 'Spanish + English',
    description: 'Search both languages',
  },
  {
    value: 'spanish',
    label: 'Spanish Only',
    description: 'Search Spanish text',
  },
  {
    value: 'metadata',
    label: 'Titles & Info',
    description: 'Search titles and verbs',
  },
]

export function SearchModeToggle({ mode, onChange, className = '' }: SearchModeToggleProps) {
  return (
    <div className={className}>
      <div className="text-xs font-medium text-white mb-2">Search Mode</div>
      <div className="flex gap-2">
        {modes.map((m) => (
          <button
            key={m.value}
            onMouseDown={(e) => {
              e.preventDefault() // Prevent blur from firing on parent input
              onChange(m.value)
            }}
            className={`
              flex-1 px-3 py-2 sm:py-2.5 text-sm sm:text-lg font-bold rounded-lg border-2 transition-colors
              ${
                mode === m.value
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-black text-white border-gray-600 hover:border-cyan-400'
              }
            `}
            title={m.description}
            aria-label={m.description}
            aria-pressed={mode === m.value}
          >
            <span className="hidden sm:inline">{m.label}</span>
            <span className="sm:hidden">
              {m.value === 'both' ? 'Both' : m.value === 'spanish' ? 'ES' : 'Info'}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
